import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [isLogin, setIsLogin] = useState(true); // always login; registration is admin-only
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const endpoint = isLogin ? '/admin/login' : '/admin/register';
      const response = await axios.post(`${API}${endpoint}`, formData);

      if (isLogin) {
        localStorage.setItem('admin_token', response.data.access_token);
        toast.success('Login successful!');
        navigate('/admin/dashboard');
      } else {
        toast.success('Registration successful! Please login.');
        setIsLogin(true);
      }

      setFormData({ username: '', password: '' });
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-24 flex items-center justify-center" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-card rounded-lg p-8 md:p-12">
          <h1 className="font-serif text-3xl md:text-4xl font-light text-center mb-8" data-testid="admin-page-title">
            {isLogin ? 'Admin Login' : 'Admin Register'}
          </h1>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-form">
            <div>
              <label htmlFor="username" className="block font-sans uppercase tracking-[0.2em] text-xs font-medium text-muted-foreground mb-2">
                Username
              </label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  data-testid="admin-username-input"
                  className="w-full pl-12 pr-4 py-3 bg-background border border-border rounded-lg font-sans text-base focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="Enter username"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block font-sans uppercase tracking-[0.2em] text-xs font-medium text-muted-foreground mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  data-testid="admin-password-input"
                  className="w-full pl-12 pr-4 py-3 bg-background border border-border rounded-lg font-sans text-base focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="Enter password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              data-testid="admin-submit-button"
              className="w-full px-8 py-4 bg-accent text-white font-sans uppercase tracking-[0.2em] text-sm font-medium rounded-full hover:bg-accent/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Please wait...' : (isLogin ? 'Login' : 'Register')}
            </button>
          </form>

          {/* Public self-registration removed for security: new admins are created by an existing admin. */}
        </div>
      </motion.div>
    </div>
  );
};

export default Admin;